import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { cssTransition } from 'react-toastify';
import { AnalyticsProvider } from '@steelbuy/analytics';
import { logout, Tenant } from '@steelbuy/api-integration';
import { Authenticated, AuthProvider, RefreshToken, Unauthenticated } from '@steelbuy/auth';
import { useUserSelfDetails } from '@steelbuy/data-access';
import { ErrorHandler } from '@steelbuy/error';
import { LoadSelfUser } from '@steelbuy/ui-domain';

import { ToastContainerId, ToasterContainer } from '@steelbuy/ui-primitive';
import { AppMainLayout } from './components/app-layout/app-main-layout/AppMainLayout';
import { AppLayout } from './components/app-layout/AppLayout';
import { LocationAwareGenericError } from './components/generic-error/LocationAwareGenericError';
import { LanguageSelection } from './components/language-selection/LanguageSelection';
import { Navigation } from './components/navigation/Navigation';
import { ScrollPositionContextProvider } from './components/scroll-position/ScrollPositionContext';
import { constants } from './constants';
import { LoginRouter } from './router/LoginRouter';
import { Router } from './router/Router';
import { UnauthenticatedRouter } from './router/UnauthenticatedRouter';
import { UNAUTHENTICATE_SIGNAL } from '../store/store';

import 'react-toastify/dist/ReactToastify.css';

const modalTransition = cssTransition({
    enter: 'zoom-in',
    exit: 'noop',
    collapse: false,
});

export const App = () => {
    const { t } = useTranslation('translation');
    const dispatch = useDispatch();
    const { data: userModel } = useUserSelfDetails();

    useEffect(() => {
        document.title = t('application.pageTitle');
    }, [navigator.language]);

    const handleReauthenticate = () => {
        dispatch({ type: UNAUTHENTICATE_SIGNAL });
    };

    const handleUnauthenticate = async (refreshToken?: RefreshToken, queryParam?: string) => {
        dispatch({ type: UNAUTHENTICATE_SIGNAL });
        refreshToken &&
            userModel &&
            (await logout(constants.apiBaseUrl, refreshToken, Tenant.MARKETPLACE, userModel.id));
        // use window.location so we reset state
        let location = 'login';
        if (queryParam) {
            location += `?${queryParam}`;
        }
        window.location.assign(location);
    };

    const handleError = (error: Error, resetError: () => void) => (
        <LocationAwareGenericError error={error} resetError={resetError} />
    );

    return (
        <AuthProvider onReauthenticate={handleReauthenticate} onUnauthenticate={handleUnauthenticate}>
            <LoginRouter>
                <Authenticated>
                    <LoadSelfUser>
                        <LanguageSelection>
                            <AnalyticsProvider>
                                <AppLayout>
                                    <Navigation />
                                    <AppMainLayout>
                                        <ErrorHandler errorComponent={handleError}>
                                            <ScrollPositionContextProvider>
                                                <Router />
                                            </ScrollPositionContextProvider>
                                        </ErrorHandler>
                                    </AppMainLayout>
                                </AppLayout>
                            </AnalyticsProvider>
                        </LanguageSelection>
                    </LoadSelfUser>
                </Authenticated>
                <Unauthenticated>
                    <AppLayout>
                        <>
                            <Navigation isAuthenticated={false} />
                            <AppMainLayout>
                                <ErrorHandler errorComponent={handleError}>
                                    <UnauthenticatedRouter />
                                </ErrorHandler>
                            </AppMainLayout>
                        </>
                    </AppLayout>
                </Unauthenticated>
            </LoginRouter>
            <ToasterContainer />
            <ToasterContainer containerId={ToastContainerId.MODAL} transition={modalTransition} />
            <ToasterContainer containerId={ToastContainerId.CRITICAL} />
        </AuthProvider>
    );
};
